import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { VStack, Heading, Text, Spinner, Box, Icon, Button } from "@chakra-ui/react";
import { CheckCircle, XCircle, AlertCircle } from "lucide-react";
import API from "../Utils/api";
import { ADD_BALANCE_PARAMS } from "../Utils/Constants";
import { setDynamicTitle } from "../Utils/dynamicTitleManager";

export const EmailWithdrawAction = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [message, setMessage] = useState("");
  const [actionType, setActionType] = useState("");

  useEffect(() => {
    setDynamicTitle("Withdrawal Action");
  }, []);

  useEffect(() => {
    const withdrawId = searchParams.get("_id");
    const type = searchParams.get("type");

    if (!withdrawId || !type) {
      setStatus("error");
      setMessage("Invalid request. Missing required parameters.");
      return;
    }

    if (type !== "accept" && type !== "reject") {
      setStatus("error");
      setMessage("Invalid action type. Must be 'accept' or 'reject'.");
      return;
    }

    setActionType(type);
    handleWithdrawAction(withdrawId, type);
  }, [searchParams]);

  const handleWithdrawAction = async (withdrawId, type) => {
    try {
      const response = await API.get(
        `/emailwithdraw-accept-reject?_id=${withdrawId}&type=${type}&paramsType=${ADD_BALANCE_PARAMS}`
      );

      const data = response?.data;

      if (data?.meta?.code === 200) {
        setStatus("success");
        setMessage(data?.meta?.message || `Withdrawal ${type}ed successfully.`);
      } else {
        setStatus("error");
        setMessage(data?.meta?.message || `Failed to ${type} withdrawal.`);
      }
    } catch (error) {
      setStatus("error");
      setMessage(
        error?.response?.data?.meta?.message ||
        error?.response?.data?.message ||
        `An error occurred while processing the ${type} request.`
      );
    }
  };

  const handleGoToWithdrawals = () => {
    navigate("/withdrawals");
  };

  const handleGoToDashboard = () => {
    navigate("/");
  };

  return (
    <Box
      minH="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={4}
    >
      <Box
        bg="white"
        rounded="lg"
        shadow="md"
        p={8}
        maxW="500px"
        w="full"
        textAlign="center"
      >
        {status === "loading" && (
          <VStack gap={6}>
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <VStack gap={2}>
              <Heading size="lg" color="gray.700">
                Processing {actionType === "accept" ? "Acceptance" : "Rejection"}
              </Heading>
              <Text color="gray.500">
                Please wait while we process your request...
              </Text>
            </VStack>
          </VStack>
        )}

        {status === "success" && (
          <VStack gap={6}>
            <Icon as={CheckCircle} boxSize={16} color="green.500" />
            <VStack gap={2}>
              <Heading size="lg" color="green.600">
                {actionType === "accept" ? "Accepted" : "Rejected"} Successfully
              </Heading>
              <Text color="gray.600">{message}</Text>
            </VStack>
            <VStack gap={3} w="full" mt={4}>
              <Button
                onClick={handleGoToWithdrawals}
                colorPalette="blue"
                w="full"
                size="lg"
              >
                View All Withdrawals
              </Button>
              <Button
                onClick={handleGoToDashboard}
                variant="outline"
                w="full"
                size="lg"
              >
                Go to Dashboard
              </Button>
            </VStack>
          </VStack>
        )}

        {status === "error" && (
          <VStack gap={6}>
            <Icon
              as={message?.toLowerCase()?.includes("already") ? AlertCircle : XCircle}
              boxSize={16}
              color="red.500"
            />
            <VStack gap={2}>
              <Heading size="lg" color="red.600">
                {message?.toLowerCase()?.includes("already")
                  ? "Already Processed"
                  : "Action Failed"}
              </Heading>
              <Text color="gray.600">{message}</Text>
            </VStack>
            <VStack gap={3} w="full" mt={4}>
              <Button
                onClick={handleGoToWithdrawals}
                colorPalette="blue"
                w="full"
                size="lg"
              >
                View All Withdrawals
              </Button>
              <Button
                onClick={handleGoToDashboard}
                variant="outline"
                w="full"
                size="lg"
              >
                Go to Dashboard
              </Button>
            </VStack>
          </VStack>
        )}
      </Box>
    </Box>
  );
};
